import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import {
    LayoutDashboard,
    BookOpen,
    Activity,
    Utensils,
    TestTube2,
    Pill,
    CalendarClock,
    FlameKindling,
    Users,
    Brain,
    MessageSquare,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    BedDouble,
    Clock,
    Heart,
    Mic,
    Camera,
    Bell,
    FileDown,
    Cloud,
    Syringe,
    Lock,
    Menu,
    X
} from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { Button } from './ui/button';

const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', tier: 'free' },
    { icon: BookOpen, label: 'Knowledge Base', path: '/knowledge', tier: 'essential' },
    { icon: Mic, label: 'Voice Journal', path: '/voice-journal', tier: 'essential' },
    { icon: Camera, label: 'Photo Log', path: '/photo-log', tier: 'essential' },
    { icon: Activity, label: 'Symptom Tracker', path: '/symptoms', tier: 'essential' },
    { icon: BedDouble, label: 'Sleep Tracker', path: '/sleep', tier: 'essential' },
    { icon: Utensils, label: 'Food Tracker', path: '/food', tier: 'essential' },
    { icon: Cloud, label: 'Weather Tracker', path: '/weather', tier: 'essential' },
    { icon: FlameKindling, label: 'Flare Journal', path: '/flares', tier: 'essential' },
    { icon: Heart, label: 'Cycle Tracker', path: '/cycle', tier: 'essential' },
    { icon: Syringe, label: 'Vaccine Tracker', path: '/vaccines', tier: 'essential' },
    { icon: Users, label: 'Community', path: '/community', tier: 'essential' },
    { icon: Brain, label: 'Coping Tools', path: '/coping', tier: 'essential' },
    { icon: MessageSquare, label: 'AI Chat', path: '/chat', tier: 'essential' },
    { icon: Clock, label: 'Medical Timeline', path: '/timeline', tier: 'premium' },
    { icon: TestTube2, label: 'Lab Tracker', path: '/labs', tier: 'premium' },
    { icon: Pill, label: 'Supplements & Meds', path: '/supplements', tier: 'premium' },
    { icon: Bell, label: 'Med Reminders', path: '/medication-reminders', tier: 'premium' },
    { icon: CalendarClock, label: 'Appointments', path: '/appointments', tier: 'premium' },
    { icon: FileDown, label: 'Export Data', path: '/export', tier: 'premium' },
];

const tierColors = {
    free: 'text-slate-300',
    essential: 'text-sky-400',
    premium: 'text-violet-400'
};

const tierBadges = {
    essential: { label: 'ESS', color: 'bg-sky-500/20 text-sky-300' },
    premium: { label: 'PRO', color: 'bg-violet-500/20 text-violet-300' }
};

export const LandingSidebar = () => {
    const [collapsed, setCollapsed] = useState(true);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();

    const closeMobileMenu = () => {
        setMobileOpen(false);
    };

    const handleNavClick = (e, path) => {
        e.preventDefault();
        // Close mobile menu if open
        if (mobileOpen) {
            closeMobileMenu();
        }
        // Store the intended destination and redirect to login
        sessionStorage.setItem('redirectAfterLogin', path);
        navigate('/login');
    };

    const SidebarContent = ({ isMobile = false, onClose }) => (
        <div className={cn(
            "flex flex-col h-full",
            isMobile ? "w-full" : ""
        )}>
            {/* Logo */}
            <div className={cn(
                "flex items-center gap-3 p-4 border-b border-slate-700",
                collapsed && !isMobile ? "justify-center" : "justify-between"
            )}>
                {(!collapsed || isMobile) && (
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-sm leading-tight text-white">Autoimmune</h1>
                            <p className="text-xs text-slate-400">Pathways</p>
                        </div>
                    </div>
                )}
                {collapsed && !isMobile && (
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                )}
                {/* Desktop expand/collapse button */}
                {!isMobile && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCollapsed(!collapsed)}
                        className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                    >
                        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </Button>
                )}
                {/* Mobile close button */}
                {isMobile && onClose && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-slate-400 hover:text-white hover:bg-slate-700/50"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                )}
            </div>

            {/* Sign In Prompt */}
            {(!collapsed || isMobile) && (
                <div className="p-4 border-b border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-slate-300">Sign in to access</p>
                            <p className="text-xs text-slate-500">All features available</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {navItems.map((item) => {
                    const Icon = item.icon;
                    const badge = tierBadges[item.tier];

                    const linkContent = (
                        <a
                            href={item.path}
                            onClick={(e) => handleNavClick(e, item.path)}
                            className={cn(
                                "flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium text-sm",
                                "duration-200 cursor-pointer",
                                collapsed && !isMobile && "justify-center px-2",
                                "text-slate-400 hover:text-white hover:bg-slate-700/50"
                            )}
                            data-testid={`landing-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                            <Icon className={cn("w-5 h-5 flex-shrink-0", tierColors[item.tier])} />
                            {(!collapsed || isMobile) && (
                                <>
                                    <span className="flex-1">{item.label}</span>
                                    {badge && (
                                        <span className={cn("text-xs px-1.5 py-0.5 rounded", badge.color)}>
                                            {badge.label}
                                        </span>
                                    )}
                                </>
                            )}
                        </a>
                    );

                    if (collapsed && !isMobile) {
                        return (
                            <Tooltip key={item.path}>
                                <TooltipTrigger asChild>
                                    {linkContent}
                                </TooltipTrigger>
                                <TooltipContent side="right" className="flex items-center gap-2 bg-slate-800 border-slate-700 text-white">
                                    {item.label}
                                    {badge && (
                                        <span className={cn("text-xs px-1.5 py-0.5 rounded", badge.color)}>
                                            {badge.label}
                                        </span>
                                    )}
                                </TooltipContent>
                            </Tooltip>
                        );
                    }

                    return <div key={item.path}>{linkContent}</div>;
                })}
            </nav>

            {/* Sign In Button */}
            {(!collapsed || isMobile) && (
                <div className="p-4 border-t border-slate-700">
                    <Button
                        onClick={() => navigate('/login')}
                        className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
                    >
                        Sign In
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/plans')}
                        className="w-full mt-2 text-slate-400 hover:text-white"
                    >
                        Create Account
                    </Button>
                </div>
            )}
        </div>
    );

    // Mobile elements rendered via portal to ensure they're at the top of the DOM stacking order
    const mobileElements = (
        <>
            {/* Mobile Menu Button */}
            <Button
                variant="ghost"
                size="icon"
                className="fixed top-4 left-4 z-[9999] lg:hidden bg-slate-800/90 backdrop-blur-sm text-white hover:bg-slate-700 shadow-lg"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
            >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            {/* Mobile Overlay - covers entire screen including nav */}
            {mobileOpen && (
                <>
                    {/* Full screen black overlay */}
                    <div
                        className="fixed inset-0 bg-slate-900 z-[9989] lg:hidden"
                        style={{ top: 0, left: 0, right: 0, bottom: 0 }}
                    />
                    {/* Clickable overlay on non-sidebar area */}
                    <div
                        className="fixed inset-0 bg-black/50 z-[9990] lg:hidden"
                        onClick={closeMobileMenu}
                        onTouchEnd={closeMobileMenu}
                        style={{ left: '280px' }}
                    />
                </>
            )}

            {/* Mobile Sidebar */}
            <aside
                className={cn(
                    "fixed top-0 left-0 h-screen z-[9995] bg-slate-900 border-r border-slate-700",
                    "transform transition-transform duration-300 ease-in-out lg:hidden",
                    mobileOpen ? "translate-x-0" : "-translate-x-full",
                    "w-[280px]"
                )}
            >
                <SidebarContent isMobile={true} onClose={closeMobileMenu} />
            </aside>
        </>
    );

    return (
        <TooltipProvider delayDuration={0}>
            {/* Render mobile elements via portal to document.body for proper z-index stacking */}
            {typeof document !== 'undefined' && createPortal(mobileElements, document.body)}

            {/* Desktop Sidebar */}
            <aside
                className={cn(
                    "hidden lg:flex h-screen sticky top-0 flex-col border-r border-slate-700 bg-slate-900/95 backdrop-blur-xl",
                    "duration-300",
                    collapsed ? "w-[72px]" : "w-[260px]"
                )}
                data-testid="landing-sidebar"
            >
                <SidebarContent />
            </aside>
        </TooltipProvider>
    );
};

export default LandingSidebar;
